import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SettingsCard} from 'shells/text-messaging/settings-card';
import {Body} from 'shells/typography/body';

import {useGetInboxStatus} from '../../graphql/hooks';
import {
    getFirstMessageStatusBadgeFromInboxStatus,
    getPhoneStatusBadgeFromInboxStatus,
    getPhoneNumber,
    getIsEnrolled,
    getHasSentCampaign,
    getIsPhoneStatusValid,
} from '../../helpers';

import {getEnrollmentStatusBadge} from '../enrollment/helpers';

import {ConfirmedPhoneNumber} from './confirmed-phone-number';

type Props = {
    startedFrom: 'marketing' | 'engagement';
};

export function Home(props: Props) {
    const {inboxStatus, isLoading} = useGetInboxStatus();

    const isEnrolled = getIsEnrolled(inboxStatus);
    const phoneNumber = getPhoneNumber(inboxStatus);
    const canSendMessages = Boolean(isEnrolled && phoneNumber);
    const hasSentCampaign = getHasSentCampaign(inboxStatus);
    const isPhoneStatusValid = getIsPhoneStatusValid(inboxStatus);

    return (
        <StackLegacy spacing={8}>
            {isLoading && <LoadingPage />}
            {inboxStatus && (
                <div className='br-3 border'>
                    <div className='background-offwhite-dk flex px-16 py-12 flex-dir-col'>
                        <Body isBold={true} color='dark'>
                            Registration
                        </Body>
                    </div>
                    {isPhoneStatusValid && isEnrolled ? (
                        <div className='pad-16'>
                            <div className='flex justify-sb gap-16 pad-16 br-3 border'>
                                <Body isBold={true} color='dark'>
                                    {phoneNumber && (
                                        <ConfirmedPhoneNumber phoneNumber={phoneNumber} />
                                    )}
                                </Body>
                                <Link
                                    variant='primary'
                                    // @ts-expect-error
                                    as={RoutedLink}
                                    to={
                                        props.startedFrom === 'engagement'
                                            ? '/inbox/settings/sms/enroll'
                                            : '/marketing/sms-setup/enroll'
                                    }
                                >
                                    View form
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <div className='flex flex-dir-col gap-16 pad-16'>
                            <SettingsCard
                                header='1. Enroll your brand and campaign'
                                body={
                                    isEnrolled
                                        ? null
                                        : 'Answer some questions about your business, and details about the text messages you plan to send.'
                                }
                                action={isEnrolled ? undefined : 'Enroll'}
                                to={
                                    props.startedFrom === 'engagement'
                                        ? '/inbox/settings/sms/enroll'
                                        : '/marketing/sms-setup/enroll'
                                }
                                status={getEnrollmentStatusBadge(
                                    inboxStatus.enrollmentFormStatus.status
                                )}
                                isDisabled={false}
                            />
                            <SettingsCard
                                header='2. Select a phone number for SMS'
                                body='Sending texts from Nutshell requires a new phone number that we provide. Pick an area code and a number that looks good to you.'
                                extraInfo={
                                    phoneNumber && (
                                        <ConfirmedPhoneNumber phoneNumber={phoneNumber} />
                                    )
                                }
                                action={phoneNumber ? undefined : 'Select a number'}
                                to={
                                    props.startedFrom === 'engagement'
                                        ? '/inbox/settings/sms/phone-number'
                                        : '/marketing/sms-setup/phone-number'
                                }
                                status={getPhoneStatusBadgeFromInboxStatus(inboxStatus.phoneStatus)}
                                isDisabled={!isEnrolled}
                            />
                            {props.startedFrom === 'marketing' && (
                                <SettingsCard
                                    header='3. Send your first broadcast'
                                    body='Now that you’ve got your phone number, it’s time to send your first broadcast!'
                                    action={hasSentCampaign ? undefined : 'Create a broadcast'}
                                    to='/marketing/sms/broadcasts'
                                    status={getFirstMessageStatusBadgeFromInboxStatus(
                                        inboxStatus.firstCampaignStatus,
                                        canSendMessages
                                    )}
                                    isDisabled={!canSendMessages}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </StackLegacy>
    );
}
